import {showNotification} from 'platform/components';
import {Pattern, match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useLazyGetEventQuery} from '@dms/api/core';
import {useDownloadServiceGalleryMutation, useLazyDownloadGalleryQuery} from '@dms/api/sales';
import {
  BatchDownloadEventIdArg,
  BatchDownloadSaleVehicleEventIdArg,
  BatchDownloadServiceGalleryEventIdArg,
  BulkDownloadApiResponse,
} from '@dms/api/shared';
import {useDownloadSaleVehicleGalleryV2Mutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {notificationTypes} from '../constants/notificationTypes';
import {getMaxBytes} from '../constants/photoDownloadSizePresets';
import {downloadFile} from '../utils/downloadFile';
import {handleApiError} from '../utils/handleApiError';
import {usePhotoDownloadSizeDialog} from './usePhotoDownloadSizeDialog';

const DEFAULT_BATCH_FILE_NAME = 'batch.zip';
const OVERSIZED_FILES_LISTED_COUNT = 3;

export function useBatchDownload() {
  const [eventData, setEventData] = useState<BulkDownloadApiResponse | undefined>();
  const [initiatedEventIds, setInitiatedEventIds] = useState<Set<string>>(new Set());
  // The archive is built asynchronously and announced by a single push. If that push never
  // arrives (storage service never calls back) the progress dialog would otherwise be stuck
  // forever, so the user can dismiss it. The event stays registered — a late DONE still downloads.
  const [isProgressDismissed, setIsProgressDismissed] = useState(false);
  // The newest triggered event — the only one allowed to drive `eventData`.
  //
  // `initiatedEventIds` is deliberately a set: every event a user started must still download when
  // its push lands, including one whose dialog was dismissed. `eventData` however is a single slot
  // feeding one progress dialog. Dismissing a pending archive and starting another therefore
  // leaves two registered events racing for that slot, where the older event's terminal push would
  // otherwise overwrite the newer one's filename and count, or close its dialog while that zip is
  // still being built. Gating `eventData` writes on this id keeps the download side effect
  // per-event while the dialog tracks only the archive the user is currently watching.
  //
  // A ref, not state: it is never rendered (so it must not cost a render), and every reader is a
  // promise callback or push handler that needs the value as of *resolution* time. Held in state it
  // would be read through the closure of whichever render created the callback, which is exactly
  // the stale value this guard exists to reject.
  const activeEventIdRef = useRef<string | undefined>(undefined);

  const [triggerDownloadEvent, {isLoading: isDownloadTriggerLoading, isError: hasDownloadError}] =
    useLazyDownloadGalleryQuery();

  const [
    triggerSaleVehicleDownloadEvent,
    {isLoading: isSaleVehicleDownloadTriggerLoading, isError: hasSaleVehicleDownloadError},
  ] = useDownloadSaleVehicleGalleryV2Mutation();

  const [
    triggerServiceAlbumDownloadEvent,
    {isLoading: isServiceVehicleAlbumTriggerLoading, isError: hasServiceAlbumDownloadError},
  ] = useDownloadServiceGalleryMutation();

  const [getDownloadEvent, {isLoading: isDownloadEventLoading}] = useLazyGetEventQuery();

  const {openPhotoDownloadSizeDialog} = usePhotoDownloadSizeDialog();

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== notificationTypes.BATCH_DOWNLOAD_STATE_CHANGED) {
      return;
    }

    const {eventId, state, bulkObjectDownloadUrl} = notification.payload;
    if (!initiatedEventIds.has(eventId)) {
      return;
    }

    // There is no progress stream — the backend emits exactly one push per download, carrying
    // a terminal state. Anything that is not a DONE with a usable URL is a failure, including
    // an unrecognised future state, which must still close the progress dialog.
    const downloadUrl = isNotNilOrEmpty(bulkObjectDownloadUrl) ? bulkObjectDownloadUrl : null;

    // Only the archive the user is currently watching may move the progress dialog. A superseded
    // event still downloads and still reports its own failure — it just no longer overwrites a
    // newer archive's progress, which would close that dialog while its zip is still building.
    const isActiveEvent = eventId === activeEventIdRef.current;

    const settleDownload = (downloadEvent: BulkDownloadApiResponse) => {
      match({state, downloadUrl})
        .with({state: 'DONE', downloadUrl: Pattern.string}, (donePush) => {
          const url = new URL(donePush.downloadUrl);
          url.searchParams.set('filename', downloadEvent.data.bulkName ?? DEFAULT_BATCH_FILE_NAME);

          downloadFile(url.toString());

          // The archive still holds every photo; the ones the backend could not compress under
          // the chosen size are only reported.
          const oversizedFiles = downloadEvent.data.oversizedFiles ?? [];
          if (oversizedFiles.length > 0) {
            showNotification.warning(
              i18n.t('entity.photo.notifications.oversizedPhotos', {
                count: oversizedFiles.length,
                files: oversizedFiles
                  .slice(0, OVERSIZED_FILES_LISTED_COUNT)
                  .map((file) => file.destinationPath)
                  .join(', '),
              })
            );
          }

          if (isActiveEvent) {
            setEventData(downloadEvent);
          }
        })
        .otherwise(() => {
          // Reported even for a superseded event — a failed archive the user asked for is worth
          // hearing about, and a toast cannot be mistaken for the dialog's own state.
          showNotification.error(
            downloadEvent.errorMessage || i18n.t('entity.photo.notifications.downloadFailed')
          );
          // Clearing the event data drops isStatePending, which closes the progress dialog.
          if (isActiveEvent) {
            setEventData(undefined);
          }
        });

      setInitiatedEventIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    };

    getDownloadEvent({eventId}).unwrap().then(settleDownload).catch(handleApiError);
  });

  const handleDownload = (downloadArgs: BatchDownloadEventIdArg) => {
    setEventData(undefined);
    setIsProgressDismissed(false);
    // Released before the new id is known, so neither an in-flight older push nor an older
    // initial read can be mistaken for the archive the user just asked for.
    activeEventIdRef.current = undefined;

    return triggerDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        // throws the you need to use .unwrap() error here, due to bad rule config
        // eslint-disable-next-line eslint-js/no-restricted-syntax
        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));
        activeEventIdRef.current = triggeredEvent.eventId;

        getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            // Nothing confirms the archive started, so this first read is what opens the dialog.
            // Skipped if a newer download has since taken the dialog over.
            if (activeEventIdRef.current === triggeredEvent.eventId) {
              setEventData(downloadEvent);
            }
          })
          .catch(handleApiError);
      })
      .catch(handleApiError);
  };

  const handleSaleVehicleDownload = (downloadArgs: BatchDownloadSaleVehicleEventIdArg) => {
    setEventData(undefined);
    setIsProgressDismissed(false);
    // Released before the new id is known, so neither an in-flight older push nor an older
    // initial read can be mistaken for the archive the user just asked for.
    activeEventIdRef.current = undefined;

    return triggerSaleVehicleDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));
        activeEventIdRef.current = triggeredEvent.eventId;

        return getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            // Nothing confirms the archive started, so this first read is what opens the dialog.
            // Skipped if a newer download has since taken the dialog over — still returned
            // either way, since callers chain on it to open their own dialog.
            if (activeEventIdRef.current === triggeredEvent.eventId) {
              setEventData(downloadEvent);
            }
            return downloadEvent;
          })
          .catch(handleApiError);
      })
      .catch((error: Error & {data?: {errors?: {code?: string}[]}}) => {
        if (error?.data?.errors?.[0]?.code === 'DMS-SV-AL-0013') {
          showNotification.error(i18n.t('entity.photo.notifications.noPhotosInAlbums'));
          return;
        }
        handleApiError(error);
      });
  };

  const handleServiceAlbumDownload = (downloadArgs: BatchDownloadServiceGalleryEventIdArg) => {
    setEventData(undefined);
    setIsProgressDismissed(false);
    // Released before the new id is known, so neither an in-flight older push nor an older
    // initial read can be mistaken for the archive the user just asked for.
    activeEventIdRef.current = undefined;

    return triggerServiceAlbumDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        // throws the you need to use .unwrap() error here, due to bad rule config
        // eslint-disable-next-line eslint-js/no-restricted-syntax
        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));
        activeEventIdRef.current = triggeredEvent.eventId;

        return getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            // Nothing confirms the archive started, so this first read is what opens the dialog.
            // Skipped if a newer download has since taken the dialog over — still returned
            // either way, since callers chain on it to open their own dialog.
            if (activeEventIdRef.current === triggeredEvent.eventId) {
              setEventData(downloadEvent);
            }
            return downloadEvent;
          })
          .catch(handleApiError);
      })
      .catch(handleApiError);
  };

  // Every archive download first asks for the maximum photo size; closing that dialog downloads
  // nothing.
  const withPhotoDownloadSize =
    <TArgs extends {maxBytes?: number}, TResult>(trigger: (args: TArgs) => Promise<TResult>) =>
    async (downloadArgs: TArgs) => {
      const preset = await openPhotoDownloadSizeDialog();
      if (isNil(preset)) {
        return undefined;
      }

      return trigger({...downloadArgs, maxBytes: getMaxBytes(preset)});
    };

  const isLoading =
    isDownloadTriggerLoading ||
    isDownloadEventLoading ||
    isSaleVehicleDownloadTriggerLoading ||
    isServiceVehicleAlbumTriggerLoading;
  const isStatePending = eventData?.state === 'PENDING';

  return {
    handleDownload: withPhotoDownloadSize(handleDownload),
    handleSaleVehicleDownload: withPhotoDownloadSize(handleSaleVehicleDownload),
    handleServiceAlbumDownload: withPhotoDownloadSize(handleServiceAlbumDownload),
    eventData,
    isLoading,
    isError: hasDownloadError || hasSaleVehicleDownloadError || hasServiceAlbumDownloadError,
    isStatePending,
    /** Whether the progress dialog should be shown — honours the user dismissing it. */
    isProgressVisible: !isProgressDismissed && (isLoading || isStatePending),
    /** Closes the progress dialog without cancelling the download. */
    dismissProgress: () => setIsProgressDismissed(true),
  };
}
